<template>
  <div
    class="InputField"
    :class="rootClasses">
    <label
      v-if="label"
      :for="name">{{ label }}</label>
    <div
      :class="{ 'HasIcon': icon }"
      class="InputWrap">
      <input
        ref="inputField"
        :id="name"
        :type="type"
        :value="value"
        :placeholder="placeholder"
        :disabled="disabled"
        :name="name"
        :autocomplete="disableAutoSuggest ? 'off' : 'on'"
        @input='onInput'
        @focus="onFocus"
        @blur="onBlur"
        @keyup.enter="onEnter" />
      <div
        class="InputIcon"
        v-if="icon">
        <component :is="iconToUse" />
      </div>
      <span
        v-if="unit"
        class="Unit">{{ unit }}</span>
    </div>
    <span
      v-if="showError"
      class="ErrorText">{{ mixWB('THIS_FIELD_IS_MISSING') }}</span>
  </div>
</template>

<script>
import UserIcon from '@/assets/svg/user.svg?inline'
import PasswordIcon from '@/assets/svg/password.svg?inline'
import EventBus from '@/EventBus'

export default {
  props: {
    name: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      required: false,
      default: null,
    },
    type: {
      type: String,
      required: false,
      default: 'text',
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    unit: {
      type: String,
      required: false,
      default: '',
    },
    backgroundColor: {
      type: String,
      required: false,
      default: 'grey', // 'white' | 'grey'
    },
    focusTrigger: {
      type: Number,
      required: false,
      default: 0,
    },
    autoFocus: {
      type: Boolean,
      required: false,
      default: false,
    },
    disableAutoSuggest: {
      type: Boolean,
      required: false,
      default: false,
    },
    formGroupName: {
      type: String,
      required: false,
      default: '',
    },
    isRequired: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    label: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      showError: false,
    }
  },
  computed: {
    rootClasses() {
      return {
        BackgroundColorGrey: this.backgroundColor === 'grey',
        BackgroundColorWhite: this.backgroundColor === 'white',
      }
    },
    iconToUse() {
      if (this.icon === 'user') {
        return UserIcon
      }
      if (this.icon === 'password') {
        return PasswordIcon
      }
      return PasswordIcon
    },
  },
  watch: {
    focusTrigger() {
      this.$refs.inputField.focus()
    },
  },
  methods: {
    onInput(e) {
      this.showError = false
      this.$emit('on-update', { name: this.name, value: e.target.value })
    },
    onFocus(e) {
      this.$emit('focus', { name: this.name, value: e.target.value })
    },
    onBlur(e) {
      this.$emit('blur', { name: this.name, value: e.target.value })
    },
    onEnter(e) {
      e.target.blur()
      this.$emit('enter', { name: this.name, value: e.target.value })
    },
    onValidateField(formGroupName) {
      if (!this.isRequired || !this.formGroupName || this.formGroupName !== formGroupName) {
        return
      }

      if (this.value) {
        this.showError = false
        return
      }

      this.showError = true
    },
  },
  components: {
    UserIcon,
    PasswordIcon,
  },
  created() {
    EventBus.$on('validate-field', this.onValidateField)
  },
  mounted() {
    if (this.autoFocus) {
      requestAnimationFrame(() => {
        this.$refs.inputField.focus()
      })
    }
  },
  destroyed() {
    EventBus.$off('validate-field', this.onValidateField)
  },
}
</script>

<style lang="stylus" scoped>
  .InputField
    margin-bottom 10px
    &.BackgroundColorGrey
      input
        background-color $color_grey_lightest
    &.BackgroundColorWhite
      input
        background-color #fff
    label
      padding-bottom 5px
      display block

  .InputWrap
    position relative
    input
      height 40px
      border 1px solid $color_grey_lighter
      padding-left 10px
      padding-right 10px
    &.HasIcon
      input
        padding-left 40px
    .Unit
      position absolute
      top 0
      right 10px
      height 100%
      flex-center-children()
      color $color_grey

  .InputIcon
    position absolute
    top 0
    left 0
    width 40px
    height 40px
    padding 10px
    svg
      fill $color_grey

  .ErrorText
    display block
    color $color_error
    margin-top 5px

</style>
